import { InboxOutlined } from "@ant-design/icons";
import {
  Button,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Spin,
  Tooltip,
  UploadFile,
  UploadProps
} from "antd";
import FormItem from "antd/es/form/FormItem";
import { RcFile } from "antd/lib/upload";
import Dragger from "antd/lib/upload/Dragger";
import { useState } from "react";
import { BASE_URL } from "services/endpoints";
import "./import-schedule.scss";
import { DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import CIQDatePicker from "components/custom-date-picker";
import { SidepanelStyles } from "constants/index";

export default function ImportSchedulerTasks({
  tokenRetrievalState,
  setDrawerOpen,
  showImportLogDrawer
}: {
  tokenRetrievalState: { token: string };
  setDrawerOpen: any;
  showImportLogDrawer: boolean;
}) {
  const acceptedExtensions = ["xer", "mpp", "csv"];

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [unsupportedFileSelected, setUnsupportedFileSelected] = useState(false);
  const [form] = Form.useForm();

  const handleUpload = () => {
    const formData = new FormData();
    fileList.forEach((file) => {
      const extension =
        file?.name?.substring(
          file.name.lastIndexOf(".") + 1,
          file?.name?.length
        ) || "";

      let fileType = "P6";
      if (extension === "mpp") {
        fileType = "MSP";
      } else if (extension === "csv") {
        fileType = "CSV";
      }
      formData.append("file", file as RcFile);
      formData.append("schedule_name", form.getFieldValue("scheduleName"));
      formData.append("schedule_type", form.getFieldValue("scheduleType"));
      formData.append("file_type", fileType);
      formData.append(
        "schedule_date",
        form.getFieldValue("scheduleDate").format(DATE_FORMAT_MMDDYYYY)
      );
    });

    setUploading(true);
    setDrawerOpen(true);
    const url = `${BASE_URL}/v2/schedule/import`;

    fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        authorization: `Bearer ${tokenRetrievalState.token}`
      }
    })
      .then((res) => res.json())
      .then((result) => {
        console.log("Result:", result);
        if (result.success) {
          setDrawerOpen(false);
          message.success(result.success.message);
        } else {
          message.error(result.message ?? "import failed.", 3);
        }
        setDrawerOpen(false);
      })
      .catch(() => {
        message.error("import failed.");
      })
      .finally(() => {
        setUploading(false);
        setDrawerOpen(false);
      });
  };

  const props: UploadProps = {
    style: { borderColor: unsupportedFileSelected === true ? "red" : "" },
    accept: ".xer, .mpp, .csv",
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      if (e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        const fileName = file.name.toLowerCase();
        const extension =
          fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) ||
          "";
        if (!acceptedExtensions.some((x) => x === extension)) {
          setUnsupportedFileSelected(true);
          setFileList([]);
          message.error("Please select .XER .MPP and .CSV files only");
          return false;
        }
        setUnsupportedFileSelected(false);
        return false;
      }
      return false;
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      const extension =
        fileName.substring(fileName.lastIndexOf(".") + 1, fileName.length) ||
        "";
      if (!acceptedExtensions.some((x) => x === extension)) {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error("Please select .XER .MPP and .CSV files only");
        return false;
      }
      setUnsupportedFileSelected(false);
      setFileList([file]);
      return false;
    },
    showUploadList: {
      showDownloadIcon: false,
      showPreviewIcon: false
    }
  };

  return (
    <Modal
      className="import-schedule custom-drawer"
      title="Import Schedule"
      width={420}
      style={SidepanelStyles.style}
      bodyStyle={{ height: "calc(100vh - 58px)" }}
      footer={null}
      open={showImportLogDrawer}
      onCancel={() => {
        setDrawerOpen(false);
      }}
      destroyOnClose
      data-testid="index-modal-import-scheduler-tasks"
    >
      <div className="space-y-4 w-full px-3">
        <div className="w-full">
          <Dragger
            {...props}
            disabled={uploading}
            data-testid="upload-file-schedule"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined data-testid="upload-icon" />
            </p>
            {fileList.length > 0 ? (
              <div className="flex px-3 justify-center items-center">
                <Tooltip title={fileList[0].name}>
                  <p
                    className="ant-upload-text truncate max-w-[330px]"
                    data-testid="file-name-test"
                  >
                    {fileList[0].name}
                  </p>
                </Tooltip>
              </div>
            ) : (
              <div>
                <p
                  className="ant-upload-text"
                  data-testid="upload-instruction-text"
                >
                  Click or drag a file to upload
                </p>
                <p
                  className="ant-upload-hint"
                  data-testid="upload-file-types-hint"
                >
                  Only .xer, .mpp and .csv* files are supported.
                </p>
              </div>
            )}
          </Dragger>
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpload}
          data-testid="import-schedule-form"
        >
          <div className="flex space-x-2">
            <FormItem
              name="scheduleName"
              label="Schedule Name"
              className="w-1/2 placeholder:text-xs"
              hidden={fileList.length === 0}
              rules={[
                {
                  required: true,
                  message: "Enter schedule name"
                }
              ]}
              data-testid="schedule-name"
            >
              <Input
                className="h-9 border-solid border border-ciq-gray-800 border-opacity-30 placeholder:text-xs"
                placeholder="Enter schedule name"
                disabled={uploading}
                data-testid="schedule-name-input"
              />
            </FormItem>
            <FormItem
              name="scheduleDate"
              label="Schedule Date"
              className="w-1/2 placeholder:text-xs"
              hidden={fileList.length === 0}
              rules={[
                {
                  type: "object",
                  required: true,
                  message: "Select a date"
                }
              ]}
              data-testid="schedule-date"
            >
              <CIQDatePicker
                className="w-full h-9 border-solid border border-ciq-gray-800 border-opacity-30 date-picker-with-icon"
                placeholder="Select schedule date"
                format={DATE_FORMAT_MMDDYYYY}
                disabled={uploading}
                data-testid="schedule-date-picker"
              />
            </FormItem>
          </div>
          <FormItem
            name="scheduleType"
            className="w-1/2 pr-[6px]"
            label="Schedule Caption"
            hidden={fileList.length === 0}
            data-testid="schedule-caption"
          >
            <Input
              className="h-9 border-solid border border-ciq-gray-800 border-opacity-30 placeholder:text-xs"
              placeholder="Enter schedule caption"
              disabled={uploading}
              data-testid="schedule-caption-input"
            />
          </FormItem>

          {uploading ? (
            <div>
              <Divider className="my-2" />
              <Spin
                className="mt-4 text-ciq-gray-800 w-full"
                tip="Import is in progress, this may take some time."
                size="default"
                data-testid="import-progress-spinner"
              />
            </div>
          ) : (
            <div>
              <div className="flex justify-end space-x-2 -mt-3">
                <Button
                  onClick={() => {
                    form.resetFields();
                    setFileList([]);
                  }}
                  style={{ marginTop: 16 }}
                  disabled={uploading}
                  hidden={fileList.length === 0}
                  data-testid="clear-button"
                >
                  Clear
                </Button>
                <Button
                  disabled={uploading || fileList.length === 0}
                  loading={uploading}
                  htmlType="submit"
                  style={{ marginTop: 16 }}
                  type="primary"
                  data-testid="import-button"
                >
                  {uploading ? "Importing" : "Import"}
                </Button>
              </div>
              <Divider className="mb-2" />
              <div className="flex text-xs space-x-2 items-center">
                <div className="border border-solid border-ciq-blue-300 rounded-lg p-1 font-bold text-xs bg-ciq-blue-100">
                  .CSV
                </div>
                <div data-testid="csv-info-text">
                  *Currently, only .csv exports from vPlanner are supported.
                </div>
              </div>
            </div>
          )}
        </Form>
      </div>
    </Modal>
  );
}
